import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { NodeViewWrapper, NodeViewContent } from "@tiptap/react";
import Swal from "sweetalert2";
import { useMediaQuery } from "@mantine/hooks";
import { Button, createStyles, Box, ScrollArea, Drawer, Text, Group } from "@mantine/core";
import { useInView } from "react-intersection-observer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import tinycolor from "tinycolor2";

import Message from "../../../spaces_neo/presentations/discussion/Message";
import Reflections from "../../../spaces_neo/presentations/discussion/Reflections";
import TiptapInline from "../TiptapInline";
import InteractiveTabs from "./InteractiveTabs";
import InteractiveSortMenu from "./InteractiveSortMenu";
import InteractiveOptions from "./InteractiveOptions";
import FilterDropdown from "../../../ui/molecules/FilterDropdown";

import InteractionModel from "../../../../models/Interaction";
import SpaceModel from "../../../../models/Space";
import MessageModel from "../../../../models/Message";
import EngagementFunctionModel from "../../../../models/EngagementFunction";
import NotificationModel from "../../../../../models/NotificationModel";

import { storeJSON } from "../../../../utils/LocalDB";
import { stylesToCamelCase } from "../../../../utils/StylesToString";
import { setCookie, getCookie } from "../../../../utils/Cookies";
import { wideOrNarrow } from "../../../../utils/PageWidth";
import { colors as swalColors } from '../../../../collections/colors';
import { useDebounce } from "../../../hooks/useDebounce";
import trackMixpanel from "../../../../utils/TrackMixpanel";
import {
  closestInteraction,
  closestBlockingInteraction,
  showNextInteraction,
  beginningOfContextForInteraction,
} from "../../utils/selection";
import useActivityCounter from "../../../../utils/ActivityCounter";
import { ActionCableContext } from "../../../../components/Index";

const useStyles = createStyles((theme, { colors }) => ({
  drawer: {
    backgroundColor: colors.background_color,
  },
  drawerHeader: {
    color: colors.default_text_color,
    fontWeight: 700,
    paddingBottom: 10
  },
  nextButton: {
    borderRadius: theme.radius.xl,
    color: tinycolor(colors.primary_button_text_color).lighten(20).toString(),
    backgroundColor: colors.primary_button_background_color,
    paddingLeft: 36,
    paddingRight: 40,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: tinycolor(colors.primary_button_background_color).lighten(3).toString()
    },
  }
}));

export default function InteractiveInput(props) {
  const node = props.node;
  const editor = props.editor;
  const interactiveInputShowAll = !!node?.attrs?.interactiveInputShowAll;
  const interactiveInputPreShowAll = !!node?.attrs?.interactiveInputPreShowAll;
  const interactivePosition = node?.attrs?.interactivePosition;
  const isAdvancedEngagement = !!node?.attrs?.interactiveAdvancedEngagement;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ref, inView } = useInView({ triggerOnce: true });
  const params = useParams();
  const smallScreen = useMediaQuery("(max-width: 576px)");
  const cable = useContext(ActionCableContext);

  const user = useSelector((state) => state.user);
  const colors = useSelector((state) => state.colors);
  const { classes } = useStyles({ colors });

  const [space, setSpace] = useState(null);
  const [messages, setMessages] = useState([]);
  const [interacted, setInteracted] = useState(false);
  const [tagging, setTagging] = useState(0);
  const [showMyResponses, setShowMyResponses] = useState(false);
  const [showAllMessages, setShowAllMessages] = useState(interactiveInputShowAll);
  const [hideAllMessages, setHideAllMessages] = useState(false);
  const [showPrivate, setShowPrivate] = useState(true);
  const [aiSummary, setAiSummary] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [summaries, setSummaries] = useState([]);

  const [receiveData, setReceiveData] = useState(null);
  const [canLoadMore, setCanLoadMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dummyPostsPrompt, setDummyPostsPrompt] = useState("");

  const [openedReflections, setOpenedReflections] = useState(false);
  const [prevShowReflections, setPrevShowReflections] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [reflectionsTagging, setReflectionsTagging] = useState(false);
  const [askPrivacy, setAskPrivacy] = useState(false);

  const [currentHighlight, setCurrentHighlight] = useState(0);
  const [hasOthersResponses, setHasOthersResponses] = useState(false);
  const [hasUserResponses, setHasUserResponses] = useState(false);
  const [functions, setFunctions] = useState([]);
  const [oriFunctions, setOriFunctions] = useState([]);
  const [sort_by, setSortBy] = useState("newest");

  const viewport = useRef();
  const loaded = useRef();
  const content = useRef();
  const auxPos = useRef();
  const querySearchCount = useRef(0);
  const messagesCopy = useRef([]);
  const fixTimeout = useRef([]);
  const interaction = useRef(null);
  const blockingInteraction = useRef(null);
  const isSaving = useRef(false);
  const showNextInteractionAux = useRef(false);
  const swiperRef = useRef(null);

  const paginator = useRef({ per: 30, page: 1 });
  const lastConnectionAttempt = useRef({
    slug: null,
    state: null,
    channel: null,
  });

  const space_id_arr = space?.id ? [space.id] : [];
  const { activityCount } = useActivityCounter(
    ["pod_message", "reflection"],
    [node.attrs.id],
    ["pod_message", "pod"],
    [node.attrs.id, ...space_id_arr]
  );

  useEffect(() => {
    if (!loaded.current) {
      if (node.attrs.id == "new") {
        createIJIfNeeded();
        return;
      }
      if (interactiveInputPreShowAll) {
        setShowAllMessages(true);
      }
    }
  }, [props]);

  useEffect(() => {
    if (node.attrs.id == "new") {
      return;
    }
    const search = window.location.search;
    const interactive_slug = new URLSearchParams(search).get("interactive_slug");
    const interactive_pop_up = new URLSearchParams(search).get("interactive_pop_up");
    
    if (
      (
        (inView || interactive_slug) &&
        !space &&
        (!loaded.current || node.attrs.forceUpdate) &&
        window.location.href.split("/")[4] == node.attrs.circle_id
      ) || window.$modalSpaceOpened == node.attrs.circle_id
    ) {
      if (!editor.isEditable) {
        onFetch();
        onFetchMessages();
        onFetchFunctions();
        performCableConnection();
        if (window.$spaceUserInteractions.can_manage) {
          setShowAllMessages(true);
        }
      }

      if (!editor.isEditable) {
        if (interactive_slug && interactive_slug == node.attrs.id) {
          scrollToMe();

          if (interactive_pop_up && !isInteractedWith()) {
            Swal.fire({
              title: "Join the conversation",
              text: "Please respond to the following content section to see posts from other participants",
              confirmButtonColor: swalColors.blue,
            }).then((result) => {});
          }
        }
      }
    } else {
      if (window.location.href.split("/")[4] != node.attrs.circle_id) {
        createIJIfNeeded();
      }
    }

    const tmpShowPrivate = getCookie(`show_private_messages`);

    setShowPrivate(tmpShowPrivate == "yes" ? true : false);
  }, [inView]);

  useEffect(() => {
    if (
      !editor.isEditable &&
      inView &&
      space &&
      (window.location.href.split("/")[4] == node.attrs.circle_id || window.$modalSpaceOpened == node.attrs.circle_id)
    ) {
      onFetchMessages();
    } else {
      // Check on backend if another IJ exist with same ID
    }
  }, [showMyResponses]);

  useEffect(() => {
    if (!editor.isEditable) {
      const search = window.location.search;
      const pod_message_id = new URLSearchParams(search).get("pod_message_id");

      if (pod_message_id && interactiveInputShowAll) {
        if (querySearchCount.current == 0) {
          if (isAdvancedEngagement) {
            searchForMessage(pod_message_id);
          } else {
            if (window.$spaceUserInteractions.can_manage) {
              setShowAllMessages(true);
            }
            // setShowAllMessages(true);
            searchForMessage(pod_message_id);
          }
        } else {
          searchForMessage(pod_message_id);
        }
      }
    }
  }, [messages]);

  useEffect(() => {
    if (!loaded.current) {
      return;
    }

    let interactiveInputShowAllManager = JSON.parse(
      localStorage.getItem(`interactiveInputShowAllManager`, {})
    );
    if (!interactiveInputShowAllManager) {
      interactiveInputShowAllManager = {};
    }
    if (showAllMessages) {
      interactiveInputShowAllManager[node.attrs.uid] = showAllMessages;
    } else {
      if (interactiveInputShowAllManager[node.attrs.uid]) {
        delete interactiveInputShowAllManager[node.attrs.uid];
      }
    }
    localStorage.setItem(
      "interactiveInputShowAllManager",
      JSON.stringify(interactiveInputShowAllManager)
    );
  }, [showAllMessages]);

  useEffect(() => {
    if (receiveData && !editor.isEditable) {
      let changes = receiveData.changes;
      if (receiveData.state != "multi") {
        changes = [receiveData];
      }

      for (var i = 0; i < changes.length; i++) {
        if (changes[i].state == "new") {
          onReceivedMessage(changes[i].pod_message_id);
        } else if (changes[i].state == "update") {
          onUpdatedMessage(changes[i].pod_message_id);
        } else if (changes[i].state == "destroy") {
          onDestroyedMessage(changes[i].pod_message_id);
        } else if (changes[i].state == "pin") {
          onPinnedMessage(changes[i].pod_message_id);
        } else if (changes[i].state == "unpin") {
          onPinnedMessage(null);
        }
      }
    } else if (receiveData) {
      if (receiveData.state == "reload") {
        paginator.current.page = 1;
        onFetchMessages();
      }
    }
  }, [receiveData]);

  useEffect(() => {
    if (Array.isArray(aiSummary?.highlights) && aiSummary.highlights[currentHighlight]) {
      return;
    }
    setCurrentHighlight(0);
  }, [currentHighlight]);

  useEffect(() => {
    setCookie("show_private_messages", showPrivate ? "yes" : "no", 365);
  }, [showPrivate]);

  useEffect(() => {
    onFetchSummaries();
  }, []);

  const onChangeTab = (active) => {
    setActiveTab(active);
    if (active == 2) {
      setTimeout(() => {
        setShowAllMessages(true);
      }, 200);
    }
  };

  const createIJIfNeeded = () => {
    // create interactive journey space, recieve new id and parent
    // update id and parent attributes
    if (!window.$currentSpaceName) {
      return;
    }

    if (!editor.isEditable) {
      return;
    }

    if (fixTimeout.current) {
      clearTimeout(fixTimeout.current);
    }

    fixTimeout.current = setTimeout(() => {
      const spaceName =
        window.$currentSpaceName < 90
          ? `${window.$currentSpaceName} prompt`
          : `${window.$currentSpaceName.slice(0, 90)}... prompt`;

      SpaceModel.onCreate(
        { intention: spaceName, version: "interactive-discussion" },
        window.$currentSlug,
        (data) => {
          const { tr } = editor.view.state;
          tr.setNodeMarkup(props.getPos(), undefined, {
            ...node.attrs,
            id: data.slug,
            circle_id: window.$currentSlug,
          });
          editor.view.dispatch(tr);

          if (!window.$spaceUserInteractions) {
            window.$spaceUserInteractions = {
              position: 9999,
              can_manage: true,
              count: 1,
            };
          }
        },
        (data) => {}
      );
    }, 3000);
  };

  const performCableConnection = () => {
    lastConnectionAttempt.current.channel = cable.subscriptions.create(
      {
        channel: "PodsChannel",
        pod_slug: node.attrs.id,
        token: window.$currentUserAuthToken,
      },
      {
        connected: () => console.log("Interactive Input Channel connected: " + node.attrs.id),
        disconnected: () => {},
        received: (data) => {
          if (data.slug == node.attrs.id) {
            setReceiveData(data);
            return;
          }
        }
      }
    );
  };

  const onReceivedMessage = (messageId) => {
    MessageModel.onFetchById(
      messageId,
      node.attrs.id,
      (data) => {
        let addToHighlights = false;
        if (data.user.id != window.$currentUserId) {
          if (!isManager() && showMyResponses) {
            return;
          }
        } else {
          addToHighlights = true;
        }

        let newMessages = [...messagesCopy.current];

        if (messagesCopy.current.length == 0) {
          messagesCopy.current = [...messages];
          newMessages = [...messagesCopy.current];
        }

        let exist = false;
        for (let i = 0; i < messagesCopy.current.length; i++) {
          const msg = messagesCopy.current[i];
          if (msg.id == data.id) {
            exist = true;
            break;
          }
        }

        if (!exist) {
          newMessages.push(data);
          messagesCopy.current.push(data);
          setMessages(newMessages);
        }

        if (addToHighlights) {
          let auxAiSummary = { ...aiSummary };
          auxAiSummary.highlights.unshift(data);
          setAiSummary(auxAiSummary);

          if (typeof swiperRef.current?.slideNext === 'function') {
            setTimeout(() => {
              swiperRef.current.slideNext();
            }, 1000)
          }
        }
      },
      (data) => {}
    );
  };

  const onUpdatedMessage = (messageId) => {
    MessageModel.onFetchById(
      messageId,
      node.attrs.id,
      (data) => {
        const newMessages = [...messages];

        for (let i = 0; i < newMessages.length; i++) {
          if (newMessages[i].id == messageId) {
            newMessages[i] = data;
            break;
          }
        }

        if (selectedMessage && messageId == selectedMessage.id) {
          setSelectedMessage(data);
        }

        setMessages(newMessages);
        messagesCopy.current = [...newMessages];

        const auxAiSummary = { ...aiSummary };

        for (let i = 0; i < auxAiSummary.highlights.length; i++) {
          if (auxAiSummary.highlights[i].id == messageId) {
            auxAiSummary.highlights[i] = data;
          }
        }

        setAiSummary(auxAiSummary);
      },
      (data) => {}
    );
  };

  const onDestroyedMessage = (messageId) => {
    const newMessages = [...messages];

    if (Array.isArray(aiSummary?.highlights)) {
      setAiSummary(prevState => ({
        ...prevState,
        highlights: aiSummary.highlights.filter((msg) => msg.id != messageId)
      }));
    }

    for (let i = 0; i < newMessages.length; i++) {
      if (newMessages[i].id == messageId) {
        newMessages.splice(i, 1);
        break;
      }
    }

    setMessages(newMessages);
    messagesCopy.current = [...newMessages];
  };

  const onPinnedMessage = (messageId) => {
    const newSpace = { ...space };
    newSpace.pin_message = messageId;
  };

  const searchForMessage = (messageId) => {
    if (querySearchCount.current > 10) {
      return;
    }
    querySearchCount.current++;

    const search = window.location.search;
    const pod_message_id = new URLSearchParams(search).get("pod_message_id");
    const reflection_id = new URLSearchParams(search).get("reflection_id");
    const interactive_slug = new URLSearchParams(search).get("interactive_slug");

    if (pod_message_id && interactive_slug === node.attrs.id) {
      if (reflection_id) {
        MessageModel.onFetchById(
          messageId,
          node.attrs.id,
          (data) => {
            if (data?.pod_slug === node.attrs.id) {
  
              paginator.current.page = 1;
              onFetchMessages();
  
              onChangeTab(2);

              setPrevShowReflections(true);
              setTimeout(() => {
                setSelectedMessage(data);
                setOpenedReflections(true);
                markReflectionAsSeen(messageId);
              }, 50);
              setTimeout(() => {
                window.history.replaceState(null, null, location.pathname);
              }, 1000);
            }
    
          },
          (data) => {}
        );
      } else {
        paginator.current.page = 1;
        onFetchMessages();
        onChangeTab(2);
        setTimeout(() => {
          window.history.replaceState(null, null, location.pathname);
        }, 1000);
      }
    }
  };

  const scrollToMe = () => {
    if (editor.isEditable) {
      return;
    }

    setTimeout(() => {
      try {
        const search = window.location.search;
        const pod_message_id = new URLSearchParams(search).get("pod_message_id");

        let $scrollTo = document.getElementById(`${node.attrs.uid}`);

        if (!pod_message_id) {
          const firstContextNode = beginningOfContextForInteraction(editor, props.getPos());

          if (firstContextNode) {
            $scrollTo = document.getElementById(`${firstContextNode.attrs.uid}`);
            if (!$scrollTo) {
              $scrollTo = document.querySelector(`[uid="${firstContextNode.attrs.uid}"]`);
            }
          }
        }

        const $contentScrollViewport = document
          .getElementById(`main-scroll-area`)
          .querySelectorAll("div")[0];

        const top =
          $scrollTo.offsetTop -
          $contentScrollViewport.offsetTop +
          $contentScrollViewport.scrollTop +
          80;

        $contentScrollViewport.scrollTo({
          top: top,
          behavior: "smooth",
        });
      } catch (e) {}
    }, 1000);
  };

  const onFetch = () => {
    const ctrl = new AbortController();
    SpaceModel.onFetch(
      node.attrs.id,
      ["members"],
      (data) => {
        loaded.current = true;
        setSpace({
          ...data.space,
          permissions: { ...data.permissions },
          colors: { ...data.colors },
          interactions: data.interactions ? { ...data.interactions } : false,
          tagged_with: data.tagged_with,
          tags: data.labels
        });
      },
      (data) => {},
      ctrl.signal
    );

    // MOVE
    const ctrl2 = new AbortController();
    MessageModel.onFetchAiSummary(
      node.attrs.id,
      (data) => {
        setAiSummary({ ...data });
        setDummyPostsPrompt(data.dummy_posts_prompt);
      },
      (data) => {},
      ctrl2.signal
    );
  };

  const onFetchMessages = (params) => {
    const new_sort_by = params?.new_sort_by;
    const filter_by_member = params?.filter_by_member;
    const filter_by_tag = params?.filter_by_tag;
    const search = window.location.search;
    const _tag = new URLSearchParams(search).get("tag");

    if (_tag) {
      trackMixpanel(window.$currentUsername, "Tag clicked", { space_slug: node.attrs.id }); 
    }

    MessageModel.onFetch(
      node.attrs.id,
      {
        tag: _tag,
        only_current_user: showMyResponses,
        update_last_seen: displayToTheUser() && (isAdvancedEngagement ? activeTab === 2 : true),
        sort_by: new_sort_by || sort_by,
        filter_by_member: showMyResponses ? null : filter_by_member,
        filter_by_tag
      },
      paginator.current.page,
      (data) => {
        setHasOthersResponses(data.has_others_responses); 
        setHasUserResponses(data.has_user_responses); 
        setCanLoadMore(paginator.current.page < data.pages);

        if (paginator.current.page == 1) {
          messagesCopy.current = [...data.data];
          setMessages([...data.data]);
        } else if (data.data.length > 0) {
          const auxData = data.data.concat(messagesCopy.current);
          messagesCopy.current = [...auxData];
          setMessages([...auxData]);
        }

        if (showNextInteractionAux.current) {
          showNextInteractionAux.current = false;
          showNextInteraction(editor, props.getPos());
        }
      },
      (data) => {}
    );
  };

  const onLoadMore = () => {
    paginator.current.page++;
    onFetchMessages();
  };

  const displayToTheUser = () => {
    if (window.$spaceUserInteractions) {
      if (window.$spaceUserInteractions.can_manage) {
        auxPos.current = "manage";
        return true;
      }
      try {
        interaction.current = closestBlockingInteraction(editor, props.getPos(), true);
        
        if (interaction.current) {
          auxPos.current = interaction.current.interactivePosition;
        }

        if (
          interaction.current != null &&
          window.$spaceUserInteractions.position <
            interaction.current.interactivePosition
        ) {
          return false;
        } else {
          return true;
        }
      } catch (error) {
        console.log('IJ ERROR displayToTheUser:', error);
        return false;
      }
    }
    return true;
  };

  const isActiveToTheUser = () => {
    if (window.$spaceUserInteractions) {
      if (window.$spaceUserInteractions.can_manage) {
        return true;
      }
      try {
        interaction.current = closestInteraction(editor, props.getPos(), true);

        blockingInteraction.current = closestBlockingInteraction(editor, props.getPos(), true);

        return (
          (!interaction.current &&
            !blockingInteraction.current &&
            window.$spaceUserInteractions.position == -1) ||
          (interaction.current != null &&
            window.$spaceUserInteractions.position ==
              interaction.current.interactivePosition) ||
          (blockingInteraction.current != null &&
            window.$spaceUserInteractions.position >=
              blockingInteraction.current.interactivePosition)
        );
      } catch (error) {
        console.log('IJ ERROR isActiveToTheUser:', error);
        return false;
      }
    }
    return false;
  };

  const isInteractedWith = () => {
    if (window.$spaceUserInteractions) {
      if (window.$spaceUserInteractions.can_manage) {
        return true;
      }
      try {
        return window.$spaceUserInteractions.position >= interactivePosition;
      } catch (error) {
        console.log('IJ ERROR isInteractedWith:', error);
        return false;
      }
    }
    return false;
  };

  const isManager = () => {
    return window.$spaceUserInteractions && window.$spaceUserInteractions.can_manage;
  };

  const onContentUpdate = (_content, html, clearCallback, isEmpty) => {
    if (isEmpty) {
      content.current = null;
      return;
    }

    content.current = {
      json: _content.content,
      html: html,
      clear: clearCallback,
    };
  };

  const onSetAskPrivacy = () => {
    if (!content.current) {
      return;
    }
    setAskPrivacy(true);
  };

  const onSave = (privacy = "open") => {
    if (!content.current || isSaving.current) {
      return;
    }
    if (
      !window.$spaceUserInteractions.logged_in &&
      !window.$spaceUserInteractions.can_manage
    ) {
      localStorage.setItem("join_slug", params.slug);
      navigate(`/user/start?s=${params.slug}`);
      return;
    }
    if (editor && !editor.isEditable && !saving) {
      isSaving.current = true;
      setSaving(true);

      setTimeout(() => {
        if (
          !content.current.json ||
          (content.current.json[0] &&
            content.current.json[0].type == "paragraph" &&
            !content.current.json[0].content)
        ) {
          isSaving.current = false;
          setSaving(false);
          return;
        }
        const json = {
          attrs: {
            uid: `message-${new Date().getTime()}`,
          },
          type: "paragraph",
          content: content.current.json,
        };
        const html = content.current.html;

        MessageModel.onCreate(
          node.attrs.id,
          { json, html, privacy, auto_tag: node.attrs.autoTagMessage, auto_tag_ai: node.attrs.autoTagAI },
          (data) => {
            setAskPrivacy(false);
            isSaving.current = false;
            setSaving(false);
            content.current.clear();
            storeJSON("spaces", `interactive-input-${node.attrs.id}`, {
              json: "",
              html: "",
            });

            content.current = null;

            if (window.$spaceUserInteractions.can_manage) {
              setShowAllMessages(true);
              return;
            }

            if (isAdvancedEngagement) {
              if (interactiveInputShowAll) {
                setShowAllMessages(true);
              }
              onSaveInterationOnly();
              setInteracted(true);
              onPromptResponseScroll();
              return;
            }

            if (!isInteractedWith()) {
              onSaveInteration();
            } else {
              if (interactiveInputShowAll) {
                setShowAllMessages(true);
              }
            }
          },
          (data) => {
            isSaving.current = false;
            setSaving(false);
          }
        );
      }, 550);
    }
  };

  const onSaveInterationOnly = () => {
    InteractionModel.onCreate(
      params.slug,
      {
        view_id: `circle-content-${params.slug}`,
        position: interactivePosition,
      },
      (data) => {
        console.log("onSaveInterationOnly ===> ", window.$spaceUserInteractions);
      },
      (data) => {}
    );
  };

  const onSaveInteration = () => {
    InteractionModel.onCreate(
      params.slug,
      {
        view_id: `circle-content-${params.slug}`,
        position: interactivePosition,
      },
      (data) => {
        window.$spaceUserInteractions = {
          count: window.$spaceUserInteractions
            ? window.$spaceUserInteractions.count
            : 1,
          position: interactivePosition,
          logged_in: window.$spaceUserInteractions
            ? window.$spaceUserInteractions.logged_in
            : false,
        };

        window.$spaceUserInteractions.position = interactivePosition;
        showNextInteractionAux.current = true;

        if (interactiveInputShowAll) {
          setShowAllMessages(true);
        }

        showNextInteraction(editor, props.getPos(), false);

        setInteracted(false);
        setInteracted(true);

        if (data.auto_joined) {
          // setTimeout(() => {
          //   navigate(`/space/${params.slug}?refresh=true`);
          // }, 1000);
        }
      },
      (data) => {}
    );
  };

  const onSkip = () => {
    if (editor && !editor.isEditable) {
      if (window.$spaceUserInteractions.can_manage) {
        return;
      }
      if (!window.$spaceUserInteractions.logged_in) {
        localStorage.setItem("join_slug", params.slug);
        navigate(`/user/start?s=${params.slug}`);

        return;
      }

      if (isAdvancedEngagement) {
        onMoveOn();
        return;
      }

      InteractionModel.onCreate(
        params.slug,
        {
          view_id: `circle-content-${params.slug}`,
          position: interactivePosition,
        },
        (data) => {
          window.$spaceUserInteractions = {
            count: window.$spaceUserInteractions
            ? window.$spaceUserInteractions.count
            : 1,
            position: interactivePosition,
            logged_in: window.$spaceUserInteractions
              ? window.$spaceUserInteractions.logged_in
              : false,
          };
          showNextInteraction(editor, props.getPos());
          setInteracted(true);
        },
        (data) => {}
      );
    }
  };

  const onOpenReflections = (messageId) => {
    setPrevShowReflections(true);
    setTimeout(() => {
      setOpenedReflections(true);
      MessageModel.onFetchById(
        messageId,
        node.attrs.id,
        (data) => {
          if (data?.pod_slug === node.attrs.id) {
            setSelectedMessage(data);
            markReflectionAsSeen(messageId);
          }
        },
        (err) => {}
      );
    }, 50);
  };

  const markReflectionAsSeen = (messageId) => {
    NotificationModel.onMarkAsSeen("reflection", messageId, (data) => {});
  };

  const openMessages = () => {
    let filtered_messages = [];
    if (isManager()) {
      if (showPrivate) {
        filtered_messages = messages.filter((msg) => msg.privacy == "open");
      } else {
        filtered_messages = messages.filter((msg) => !msg.is_private)
      }
    } else {
      filtered_messages = messages.filter((msg) => msg.privacy == "open");
    }
    return filtered_messages.reverse();
  };

  const hasPrivateMessages = () => {
    return messages.filter((msg) => msg.is_private).length > 0;
  };

  const onMoveOn = () => {
    if (!window.$spaceUserInteractions.logged_in) {
      localStorage.setItem("join_slug", params.slug);
      navigate(`/user/start?s=${params.slug}`);
      return;
    }

    if (!isInteractedWith()) {
      onSaveInteration();
    }

    setTimeout(() => {
      const _pos = props.getPos();
      const viewport = document.querySelector(
        "#main-scroll-area .mantine-ScrollArea-viewport"
      );
      if (viewport) {
        const _node = editor.view.state.doc.nodeAt(_pos + 1);
        let element = document.getElementById(_node.attrs.uid);
        if (!element) {
          element = document.querySelector(`[uid="${_node.attrs.uid}"]`);
        }
        if (element) {
          const rect = element.getBoundingClientRect();
          const scrollTop = rect.top + viewport.scrollTop - viewport.getBoundingClientRect().top;
          const top = scrollTop - 50;
          if (top > 100) {
            viewport.scrollTo({ top: top });
          }
        }
      }
    }, 1000);
  };

  const onPromptResponseScroll = () => {
    setTimeout(() => {
      const _pos = props.getPos();
      const viewport = document.querySelector(
        "#main-scroll-area .mantine-ScrollArea-viewport"
      );
      if (viewport) {
        const _node = editor.view.state.doc.nodeAt(_pos);
        let element = document.getElementById(_node.attrs.uid);
        if (!element) {
          element = document.querySelector(`[uid="${_node.attrs.uid}"]`);
        }
        viewport.scrollBy(0, 130);
      }
    }, 1000);
  };

  const onFetchFunctions = () => {
    EngagementFunctionModel.onFetch(
      (data) => {
        setFunctions(data.map((item) => ({ label: item.name, value: item.slug })));
        setOriFunctions(data);
      },
      (data) => {}
    );
  };

  const onFetchSummaries = () => {
    EngagementFunctionModel.onFetchSummaries(
      (data) => {
        if (Array.isArray(data)) {
          setSummaries(data);
        }
      },
      (err) => {}
    );
  };

  const onChangeSortBy = (value) => {
    paginator.current.page = 1;
    setSortBy(value);
    onFetchMessages({ new_sort_by: value });
  };

  const onSearchByMember = (value) => {
    paginator.current.page = 1;
    onFetchMessages({ filter_by_member: value });
  };

  const onSearchByTag = (value) => {
    paginator.current.page = 1;
    onFetchMessages({ filter_by_tag: value });
  };

  const onDeleteHighlightMessage = (message) => {
    if (!message?.id) return;
    MessageModel.onRemoveHighlight(
      { pod_message_id: message.id },
      (data) => {
        setPrevShowReflections(false);
        setOpenedReflections(false);
        setSelectedMessage(null);
        onFetch();
        Swal.fire({
          title: "Success",
          text: "Highlight removed.",
          confirmButtonColor: swalColors.blue
        }).then((result) => {});
      },
      (err) => {}
    );
  };

  const onAddHighlightMessage = (message) => {
    if (!message?.id) return;
    MessageModel.onAddHighlight(
      { pod_message_id: message.id, space_id: node.attrs.id },
      (data) => {
        onFetch();
        Swal.fire({
          title: "Success",
          text: "Highlight added.",
          confirmButtonColor: swalColors.blue
        }).then((result) => {});
      },
      (err) => {
        Swal.fire({
          title: "Error",
          text: err.message || "Failed to add highlight.",
          confirmButtonColor: swalColors.blue
        }).then((result) => {});
      }
    );
  };

  const onSelectTag = (tag) => {
    onSearchByTag(tag.name);
  };

  const PrimaryButton = ({ label, onClick, ...rest }) => {
    return (
      <Button
        color="dark"
        radius="xl"
        compact
        variant="outline"
        sx={(theme) => ({
          color: colors.primary_button_background_color,
          border: `1px solid ${colors.primary_button_background_color}`,
          "&:hover": {
            backgroundColor: tinycolor(colors.background_color).darken(3).toString(),
          }
        })}
        onClick={onClick}
        {...rest}
      >
        {label}
      </Button>
    );
  };

  const SecondaryButton = ({ label, onClick, backgroundColor, textColor, ...rest }) => {
    const radius = node.attrs.radius;
    return (
      <Button
        radius={radius == "circle-radius" ? "xl" : radius == "with-radius" ? "sm" : "xs"}
        sx={(theme) => ({
          backgroundColor: backgroundColor || colors.secondary_button_background_color,
          color: textColor || node.attrs.secondary_button_text_color,
          border: `1px solid ${backgroundColor || colors.secondary_button_background_color}`,
          "&:hover": {
            backgroundColor: tinycolor(backgroundColor || colors.secondary_button_background_color).darken(5).toString(),
          },
        })}
        onClick={onClick}
        {...rest}
      >
        {label}
      </Button>
    );
  };

  const NextButton = () => {
    return (
      <Group noWrap position="center" spacing={10} mt={12}>
        <Button
          className={classes.nextButton}
          leftIcon={<FontAwesomeIcon icon={solid("arrow-down")} />}
          onClick={() => onMoveOn()}
        >
          {"Next section"}
        </Button>
      </Group>
    );
  };

  const debouncedSave = useDebounce(onSave, 500);
  const debouncedSearchMember = useDebounce(onSearchByMember, 500);
  const debouncedSearchTag = useDebounce(onSearchByTag, 500);

  const userHighlights = Array.isArray(aiSummary?.highlights) && aiSummary.highlights.filter(
    (h) => h.user.username !== "sutrabot"
  );

  const highlightMessage = selectedMessage && Array.isArray(aiSummary?.highlights) && aiSummary.highlights.find(
    (h) => h.id === selectedMessage.id && !h.is_last_ij_user_message
  );

  return (
    <NodeViewWrapper
      className="react-component"
      id={node.attrs.uid}
      dataUid={node.attrs.uid}
      spaceId={node.attrs.id}
      parentId={node.attrs.circle_id}
      ref={ref}
      style={displayToTheUser() ? { ...stylesToCamelCase(node.attrs.styles) } : {}}
      isActiveToTheUser={isActiveToTheUser() ? "yes" : "no"}
      isInteractedWith={isInteractedWith() ? "yes" : "no"}
      openMessages={openMessages().length}
      displayToTheUser={displayToTheUser() ? "yes" : "no"}
    >
      {displayToTheUser() && (
        <NodeViewContent
          contentEditable="false"
          style={{
            margin: "0 auto",
            maxWidth: wideOrNarrow(),
            textAlign: node.attrs.textAlign,
            padding: smallScreen ? "0" : "0 16px"
          }}
          className={`node-content ${node.attrs.class} interactive-input-wrapper`}
          radius={node.attrs.radius}
        >
          <Box>
            {space &&
              ((openMessages().length > 0 &&
                (
                  !isActiveToTheUser()
                  || isInteractedWith()
                  || interacted
                  || interactiveInputPreShowAll
                )
              ) || isManager())
              && editor && !editor.isEditable && (
                <>
                  {isAdvancedEngagement && userHighlights.length > 0 && (
                    <>
                      <InteractiveTabs
                        node={node}
                        editor={editor}
                        swiperRef={swiperRef}
                        space={space}
                        aiSummary={aiSummary}
                        activeTab={activeTab}
                        onChangeTab={onChangeTab}
                        activityCount={activityCount}
                        dummyPostsPrompt={dummyPostsPrompt}
                        setDummyPostsPrompt={setDummyPostsPrompt}
                        functions={functions}
                        oriFunctions={oriFunctions}
                        onOpenReflections={onOpenReflections}
                        onUpdatedMessage={onUpdatedMessage}
                        tagging={tagging}
                        summaries={summaries}
                        setTagging={setTagging}
                        isManager={isManager()}
                        onMoveOn={onMoveOn}
                        onFetch={onFetch}
                      />
                      
                      {!isInteractedWith() && !isManager() && interacted && activeTab !== 2 && (
                        <NextButton />
                      )}
                    </>
                  )}
                </>
              )}
            {editor && editor.isEditable ? (
              <>
                <Box mt={10} />
                <TiptapInline
                  editable={false}
                  content={""}
                  modals={false}
                  onUpdate={onContentUpdate}
                  exclude={["formatbar"]}
                  placeholder={
                    node.attrs.interactiveInputPlaceholder ||
                    t("tiptap.components.interactiveInput.placeholder")
                  }
                  contextDb={`interactive-input-${node.attrs.id}`}
                />
                <Group position="apart" mt={10} sx={{ alignItems: "center" }}>
                  <Box>
                    <SecondaryButton
                      backgroundColor={node.attrs.buttonBackgroundColor}
                      textColor={node.attrs.buttonTextColor}
                      label={`${node.attrs.buttonText}`}
                    />
                    {node.attrs.interactiveInputAllowSkip && (
                      <SecondaryButton ml={10} label={"Skip"} />
                    )}
                  </Box>
                  <Text color="dimmed" size="sm">{`Prompt ${node.attrs.id}`}</Text>
                </Group>
              </>
            ) : activeTab === 1 || activeTab === 3 ? null : (
              isActiveToTheUser() && !isInteractedWith() && !interacted) ||
              (displayToTheUser() && openMessages().length == 0 && node.attrs.interactiveInputAllowMultipost) ||
              (displayToTheUser() && openMessages().length == 0 && !node.attrs.isBlocking) ||
              (displayToTheUser() && openMessages().length == 0 && isInteractedWith()) ||
              node.attrs.interactiveInputAllowMultipost ||
              isManager() ? (
              <>
                <Box mt={10} />
                {!askPrivacy ? (
                  <>
                    <TiptapInline
                      editable={true}
                      content={""}
                      modals={false}
                      onUpdate={onContentUpdate}
                      exclude={["formatbar"]}
                      clearCallback={true}
                      placeholder={
                        node.attrs.interactiveInputPlaceholder ||
                        t("tiptap.components.interactiveInput.placeholder")
                      }
                      contextDb={`interactive-input-${node.attrs.id}`}
                      canToggleFormatbar={true}
                      withSaveButton={true}
                      onSave={
                        interactiveInputShowAll &&
                        node.attrs.interactiveInputWithPrivacyCheck
                          ? () => onSetAskPrivacy()
                          : () => debouncedSave()
                      }
                      saveButtonText={props.node.attrs.buttonText}
                    />

                    {node.attrs.interactiveInputAllowSkip && !isInteractedWith() && (
                      <SecondaryButton
                        mt={10}
                        ml={10}
                        label={"Skip"}
                        onClick={() => onSkip()}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <Box
                      sx={{
                        borderTop: "1px solid #efefef",
                        borderBottom: "1px solid #efefef",
                        padding: "20px 0px 25px 0px",
                      }}
                    >
                      <Text weight="bold" mb={10}>
                        {t("tiptap.components.interactiveInput.text")}
                      </Text>
                      <SecondaryButton
                        disabled={isSaving.current || saving}
                        backgroundColor={node.attrs.buttonBackgroundColor}
                        textColor={node.attrs.buttonTextColor}
                        label={"Yes"}
                        onClick={() => debouncedSave("open")}
                      />
                      <SecondaryButton
                        disabled={isSaving.current || saving}
                        mt={10}
                        ml={10}
                        label={"No"}
                        onClick={() => debouncedSave("private")}
                      />
                    </Box>
                  </>
                )}
              </>
            ) : (
              <></>
            )}

            {space && (
              isInteractedWith() || interacted || interactiveInputPreShowAll || isManager()
            ) && editor && !editor.isEditable && (
              <>
                {(activeTab === 2 || !isAdvancedEngagement) && (
                  <Box>
                    <>
                      <Group my={20} spacing={10}>
                        {interactiveInputShowAll && (
                          <InteractiveSortMenu
                            isAdvancedEngagement={isAdvancedEngagement}
                            sort_by={sort_by}
                            onChangeSortBy={onChangeSortBy}
                          />
                        )}
                        {!showMyResponses && showAllMessages && (
                          <FilterDropdown
                            label="Members"
                            colors={colors}
                            onSearch={debouncedSearchMember}
                          />
                        )}
                        {Array.isArray(space?.tags) && space.tags.length > 0 && (
                          <FilterDropdown
                            label="Tag"
                            colors={colors}
                            dropdownData={space.tags}
                            onSearch={debouncedSearchTag}
                          />
                        )}
                        <Box sx={{ flex: 1 }} />

                        <InteractiveOptions
                          canShowMyResponses={hasUserResponses && (showAllMessages || isManager()) && !hideAllMessages}
                          canShowPrivate={isManager() && hasPrivateMessages() && !hideAllMessages}
                          canShowAllResponses={openMessages().length > 0 && (showAllMessages || isManager())}
                          showMyResponses={showMyResponses}
                          setShowMyResponses={setShowMyResponses}
                          showPrivate={showPrivate}
                          setShowPrivate={setShowPrivate}
                          hideAllMessages={hideAllMessages}
                          setHideAllMessages={setHideAllMessages}
                        />

                      </Group>

                      <ScrollArea style={{ height: "100%" }} viewportRef={viewport}>
                        {!hideAllMessages && openMessages().map((message) => {
                          if (showAllMessages || isManager() || (message.user?.id && message.user.id === user?.id)) {
                            const _highlightMessage = Array.isArray(aiSummary?.highlights) && aiSummary.highlights.find(
                              (h) => h.id === message.id && !h.is_last_ij_user_message
                            );
                            return (
                              <Message
                                key={`message_${message.id}`}
                                spaceId={space.slug}
                                spaceVersion={space.version}
                                messageId={message.id}
                                htmlId={`${message.type}_${message.id}`}
                                messageCreatedAt={message.created_at}
                                messageTitle={message?.post?.subject || null}
                                messageContent={
                                  message.version == "new"
                                    ? message.content
                                    : message.message_body
                                }
                                messageUnseenCount={message.unseen_count}
                                tags={message.tags}
                                reflections={message.reflections}
                                user={message.user ? message.user : {}}
                                onOpenReflections={onOpenReflections}
                                version={message.version}
                                canPin={false}
                                canTag={message?.user?.can_tag || false}
                                canLike={message?.user?.can_like || false}
                                canReflect={true}
                                canEdit={message?.user?.can_edit || false}
                                canDelete={message?.user?.can_delete || false}
                                onUpdated={onUpdatedMessage}
                                type={"pod_message"}
                                tagging={tagging}
                                setTagging={setTagging}
                                isPinned={space.pin_message == message.id}
                                canSetOpen={message.can_set_open}
                                canSetPrivate={message.can_set_private}
                                isPrivate={message.is_private}
                                text={message.text}
                                isLiked={message.is_liked}
                                likesCount={message.likes_count}
                                onDeleteHighlightMessage={_highlightMessage ? () => onDeleteHighlightMessage(_highlightMessage) : null}
                                onAddHighlightMessage={!_highlightMessage && !message.is_private ? () => onAddHighlightMessage(message) : null}
                                onSelectTag={onSelectTag}
                              />
                            );
                          }
                        })}
                        {openMessages().length == 0 && (
                          <Box sx={{ textAlign: "center" }}>
                            <Text m={40} mt={30} color="dimmed">
                              {"This discussion hasn’t received any responses yet."}
                            </Text>
                          </Box>
                        )}
                      </ScrollArea>

                      {canLoadMore && showAllMessages && (
                        <Box mt={20} mb={20} sx={{ textAlign: "center" }}>
                          <PrimaryButton
                            label={t("tiptap.components.interactiveInput.loadMoreResponse")}
                            onClick={() => onLoadMore()}
                          />
                        </Box>
                      )}
                      {!isInteractedWith() && !isManager() && interacted && (
                        <NextButton />
                      )}
                    </>
                  </Box>
                )}
              </>
            )}

            {space && space.permissions && prevShowReflections && (
              <Drawer
                opened={openedReflections}
                onClose={() => {
                  setPrevShowReflections(false);
                  setOpenedReflections(false);
                  setSelectedMessage(null);
                }}
                title={t("spaces.presentations.discussion.reflectionHeader")}
                padding="xl"
                size={600}
                classNames={{
                  drawer: classes.drawer,
                  header: classes.drawerHeader,
                }}
              >
                <Reflections
                  spaceId={space.slug}
                  spaceVersion={props.spaceVersion}
                  canReflect={true}
                  message={selectedMessage}
                  onUpdatedMessage={onUpdatedMessage}
                  useReflectCallback={true}
                  tagging={reflectionsTagging}
                  setTagging={setReflectionsTagging}
                  onDeleteHighlightMessage={highlightMessage ? () => onDeleteHighlightMessage(highlightMessage) : null}
                  onAddHighlightMessage={!highlightMessage && !selectedMessage?.is_private ? () => onAddHighlightMessage(selectedMessage) : null}
                />
              </Drawer>
            )}

          </Box>
        </NodeViewContent>
      )}
    </NodeViewWrapper>
  );
}
